import { useContext, useEffect } from 'react';
import QueryString from 'query-string';
import { createFontLinkElement, getMixpanelKey, isAzoomee } from '../utils';
import { appStore } from '../state';
import { ThemeProps } from '../types';
import { useGetDefaultTheme, useGetTranslations, useGetConfig, useGetDefaultCopy, useGetAdditionalConfig, useMixpanel } from '.';

const useAppConfig = (location: Location) => {
  const { dispatch, state } = useContext(appStore);

  useEffect(() => {
    // save params in state and localStorage
    const { query } = QueryString.parseUrl(location.href);
    // set locale to English by default. If there is another locale, then it will
    // be overwritten. The params state update will trigger the translations fecth, see
    // line 38.
    dispatch({ type: 'UPDATE_QUERY_PARAMS', payload: { ...query } })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: defaultCopy, isLoading: defaultCopyIsLoading, refetch: fetchDefaultCopy } = useGetDefaultCopy({
    queryConfig: {
      enabled: false,
      onSuccess: (data: any) => {
        dispatch({
          type: 'UPDATE_FALLBACK_TRANSLATIONS',
          payload: data
        })
      }
    },
    product: state.params.config ? state.params.config : isAzoomee ? 'azoomee' : 'dvmkids'
  })

  /**
   * Theming: Both Azoomee and DaVinciKids have unique branding. We need to fecth the
   * relevant branding first (to act as a fallback) before pulling any other branding.
   * We will look at the app domain to tell us whether we are in Azoomee or DaVinciKids
   */
  const { isLoading: defaultThemeIsLoading, refetch: fetchDefaultTheme } = useGetDefaultTheme({
    config: {
      enabled: false,
      onSuccess: (data: ThemeProps) => {
        dispatch({
          type: 'UPDATE_DEFAULT_THEME',
          payload: data
        })
      }
    },
    product: state.params.config ? state.params.config : isAzoomee ? 'azoomee' : 'dvmkids'
  })

  const { isLoading: translationIsLoading, refetch: fetchTranslations } = useGetTranslations({
    config: {
      enabled: false,
      onSuccess: (data: any) => {
        dispatch({
          type: 'UPDATE_LOCALE_TRANSLATIONS',
          payload: data
        })
      }
    },
    locale: state.params.locale,
    product: state.params.config ? state.params.config : isAzoomee ? 'azoomee' : 'dvmkids'
  });

  const { isLoading: additionalConfigIsLoading, refetch: fetchAdditionalConfig } = useGetAdditionalConfig({
    configParam: state.params.config ? state.params.config : isAzoomee ? 'azoomee' : 'dvmkids',
    queryConfig: {
      // enabled: state.params.config,
      enabled: true,
      onSuccess: (data: any) => {
        dispatch({
          type: 'UPDATE_CONFIG',
          payload: data
        })
      }
    },
    queryKey: 'fetchAdditionalConfig'
  });

  const { isLoading: defaultConfigIsLoading, refetch: fetchDefaultConfig } = useGetConfig({
    product: isAzoomee ? 'azoomee' : 'dvmkids',
    queryConfig: {
      /**
       * Uncomment this when config is available in S3
       */
      // enabled: state.params.config,
      enabled: false,
      onSuccess: async (data: any) => {
        dispatch({
          type: 'UPDATE_CONFIG',
          payload: data
        })

        await fetchDefaultCopy()
        await fetchDefaultTheme()
        if (state.params.config) {
          fetchAdditionalConfig()
        }
        if (state.params.locale) {
          await fetchTranslations()
        }
      }
    },
    queryKey: 'fetchConfig'
  })

  const {mixpanel} = useMixpanel(getMixpanelKey()!);

  useEffect(() => {
    fetchDefaultConfig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch fonts
  useEffect(() => {
    if (state.config.theme.fonts.urls.length) {
      createFontLinkElement(state.config.theme.fonts.urls);
    }
  }, [state.config.theme.fonts.urls])

  return {
    loading: defaultConfigIsLoading || defaultCopyIsLoading || defaultThemeIsLoading || translationIsLoading || additionalConfigIsLoading || !defaultCopy,
    mixpanel: mixpanel,
  };
};

export default useAppConfig;
